/**
 * ## Importing
 *
 * ### es
 *
 * ```js
 * import * as brokerStore from '@paychex/state-broker-store';
 * import { create } from '@paychex/state-broker-store';
 * ```
 *
 * ### cjs
 *
 * ```js
 * const brokerStore = require('@paychex/state-broker-store');
 * const { create } = require('@paychex/state-broker-store');
 * ```
 *
 * ### amd
 *
 * ```js
 * define(['@paychex/state-broker-store'], function(brokerStore) {...});
 * define(['@paychex/state-broker-store'], function({ create }) {...});
 * ```
 *
 * ```js
 * require(['@paychex/state-broker-store'], function(brokerStore) {...});
 * require(['@paychex/state-broker-store'], function({ create }) {...});
 * ```
 *
 * ### iife
 *
 * ```js
 * const brokerStore = window['@paychex/state-broker-store'];
 * const { create } = window['@paychex/state-broker-store'];
 * ```
 *
 * @module index
 * @example
 * ```js
 * const store = stores.memoryStore();
 * const broker = create(store);
 *
 * market.use(broker);
 * ```
 */

import { isEqual } from 'lodash';
import { Events } from '@paychex/state';
import rcompare from 'semver/functions/rcompare';
import satisfies from 'semver/functions/satisfies';

import type { SemVer } from 'semver';
import type { Store } from '@paychex/core/types/stores';
import type { Broker, Exchange } from '@paychex/state/types/exchange';

export type { Store, Broker, Exchange };

function isMatch(version: string|SemVer) {
    return satisfies(version, this);
}

/**
 * Creates a Store-backed broker for use on an Exchange.
 *
 * @function
 * @param store The {@link Store} instance to use.
 * @returns A Broker instance that can be passed to an {@link Exchange}.
 */
export function create(store: Store): Broker {

    async function updateCache(key: string, from: string, snapshot: any) {
        const map = await store.get(key) || Object.create(null);
        map[from] = snapshot;
        // update other cached versions
        for (const to in map) {
            const stale = map[to];
            if (isEqual(stale, snapshot))
                continue;
            const results = await this.fire(Events.CONVERT, key, from, to, snapshot);
            const result = results.find(Boolean) || [];
            const converted = result[1];
            if (!converted || isEqual(stale, converted))
                continue;
            map[to] = converted;
            await this.fire(Events.UPDATE, key, to, converted);
        }
        await store.set(key, map);
    }

    async function checkCache(key: string, pattern: string) {
        const map = await store.get(key) || Object.create(null);
        const versions = Object.keys(map).sort(rcompare);
        // find first version that satisfies
        const version = versions.find(isMatch, pattern);
        if (version)
            return [version, map[version]];
        // else find highest version and convert
        const from = versions.shift();
        if (from in map) {
            const results = await this.fire(Events.CONVERT, key, from, pattern, map[from]);
            const result = results.find(Boolean);
            if (result)
                await this.fire(Events.SET, key, ...result);
            return result;
        }
    }

    return function broker(bus) {
        bus.on(Events.GET, checkCache.bind(bus));
        bus.on(Events.SET, updateCache.bind(bus));
    };

}